<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("supplier") }}</h4>
        </div>
      </div>
      <div class="page-btn d-flex gap-2 item-io-btns">
        <button
          v-if="can('purchase.supplier', 'R')"
          type="button"
          class="btn btn-item-export"
          :disabled="exporting"
          @click="exportSuppliers"
        >
          <span v-if="exporting" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="ti ti-database-export me-1"></i>{{ $t("export_supplier") }}
        </button>
        <router-link v-if="can('purchase.supplier', 'W')" to="/inventory/supplier/import" class="btn btn-item-import">
          <i class="ti ti-database-import me-1"></i>{{ $t("import_supplier") }}
        </router-link>
        <a
          v-if="can('purchase.supplier', 'W')"
          href="#"
          class="btn btn-added"
          data-bs-toggle="modal"
          data-bs-target="#create-modal"
        >
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("add_new_supplier") }}
        </a>
        <CreateModal id="create-modal" @create_success="reloadTable" />
      </div>
    </div>

    <ThemeDataTable
      ref="supplierTable"
      table-id="SupplierTable"
      :fetch-data="fetchSuppliersPage"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("supplier_name") }}</th>
            <th>{{ $t("supplier_address") }}</th>
            <th>{{ $t("supplier_phone_number") }}</th>
            <th class="no-sort text-end">{{ $t("action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="supplier in rows" :key="supplier.id">
            <td>{{ supplier.name }}</td>
            <td>{{ supplier.address }}</td>
            <td>{{ supplier.phone_number }}</td>
            <td class="action-table-data text-end">
              <div v-if="can('purchase.supplier', 'E')" class="edit-delete-action justify-content-end">
                <a class="me-2 p-2" href="#" @click.prevent="openEditModal(supplier)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ti ti-edit">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>

    <EditModal id="edit-modal" :selectedSupplier="selectedSupplier" @edit_success="reloadTable" />
  </div>
</template>

<script>
import { can as canAccess } from "@/helpers/permission";
import CreateModal from '@/views/admin_dashboard/inventory_supplier/CreateModal.vue';
import EditModal from '@/views/admin_dashboard/inventory_supplier/EditModal.vue';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import apiUrls from '@/urls';

export default {
  components: {
    CreateModal,
    EditModal,
    ThemeDataTable,
  },
  data() {
    return {
      selectedSupplier: {},
      exporting: false,
    };
  },
  methods: {
    openEditModal(supplier) {
      this.selectedSupplier = supplier;
      this.$nextTick(() => {
        const modal = new bootstrap.Modal(document.getElementById('edit-modal'));
        modal.show();
      });
    },

    reloadTable() {
      this.$refs.supplierTable?.reload?.();
    },

    async fetchSuppliersPage({ draw, start, length, search, orderColumn, orderDir }) {
      const params = {
        draw,
        start,
        length,
        'search[value]': search || '',
        'order[0][column]': orderColumn,
        'order[0][dir]': orderDir,
      };

      const response = await this.$axios.get(apiUrls.inventory_supplier.get, { params });
      const payload = response.data || {};

      return {
        data: payload.data || [],
        recordsTotal: payload.recordsTotal || 0,
        recordsFiltered: payload.recordsFiltered || 0,
      };
    },

    can(code, requiredLevel) {
      return canAccess(code, requiredLevel);
    },

    exportFilename(response) {
      const header = response?.headers?.['content-disposition'] || '';
      const star = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
      const plain = /(?:^|;)\s*filename="?([^";]+)"?/i.exec(header);
      let name = (star && star[1]) || (plain && plain[1]) || '';
      try {
        name = decodeURIComponent(name.trim().replace(/^"|"$/g, ''));
      } catch (e) {
        name = name.trim().replace(/^"|"$/g, '');
      }
      if (!name.toLowerCase().endsWith('.xlsx')) {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        name = `Supplier_exported_at_${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}.xlsx`;
      }
      return name;
    },

    async exportSuppliers() {
      this.exporting = true;
      try {
        const response = await this.$axios.get(apiUrls.inventory_supplier.export, { responseType: 'blob' });
        const contentType = (response.headers['content-type'] || '').toLowerCase();
        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const detail = JSON.parse(text);
          throw new Error(detail.error || detail.detail || 'Export failed');
        }
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.exportFilename(response);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        window.alert(error?.message || 'Supplier export failed.');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
:deep(table .edit-delete-action.justify-content-end) {
  justify-content: flex-end !important;
}
:deep(td.action-table-data.text-end) {
  text-align: right !important;
}
.item-io-btns .btn {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  border-radius: 8px;
}
.btn-item-export {
  background: #ecfdf5;
  border: 1px solid #10b981;
  color: #047857;
}
.btn-item-import {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  color: #1d4ed8;
}
</style>
